'use client'

import { useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useNotificationsOverview } from '@/hooks/notifications/useNotificationsQuery'
import type { NotificationKind } from '@/services/notifications/types'
import { NotificationPopover } from './NotificationPopover'
import { getKindPriority } from './kind-style'

const ARRIVAL_ANIMATION_MS = 5000

/** Cor do badge = maior severidade entre as novas. */
function severityClass(items: { kind: NotificationKind }[]): string {
  let maxPriority = -1
  let maxKind: NotificationKind = 'info'
  for (const it of items) {
    const p = getKindPriority(it.kind)
    if (p > maxPriority) {
      maxPriority = p
      maxKind = it.kind
    }
  }
  if (maxKind === 'critical' || maxKind === 'alert') return 'bg-destructive'
  if (maxKind === 'warning') return 'bg-amber-600'
  return 'bg-primary'
}

/** Sino da rail: badge com o nº de novas, clique abre o popover. */
export function NotificationBell() {
  const { data } = useNotificationsOverview()
  const count = data?.counts.new ?? 0
  const newItems = data?.new ?? []
  const display = count > 99 ? '99+' : String(count)

  const previousCountRef = useRef<number | null>(null)
  const [justArrived, setJustArrived] = useState(false)

  useEffect(() => {
    const previous = previousCountRef.current
    previousCountRef.current = count
    // Primeiro valor observado é estado inicial, não chegada.
    if (previous === null) return
    if (count > previous) {
      setJustArrived(true)
      const t = setTimeout(() => setJustArrived(false), ARRIVAL_ANIMATION_MS)
      return () => clearTimeout(t)
    }
  }, [count])

  return (
    <NotificationPopover>
      <button
        type="button"
        aria-label={count > 0 ? `Notificações (${count} novas)` : 'Notificações'}
        className="relative flex size-9 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      >
        <Bell className={cn('size-4', justArrived && 'animate-wiggle')} />
        {count > 0 && (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white',
              severityClass(newItems),
              justArrived && 'animate-pulse',
            )}
          >
            {display}
          </span>
        )}
      </button>
    </NotificationPopover>
  )
}
